import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useMemo } from "react";
import CollapsibleFormElement from "../../common/collapsible-form-element";

export type LaraGlossary = {
  id: string;
  name: string;
};

export default function LaraGlossaries({
  availableGlossaries,
  selectedGlossaries,
  setSelectedGlossaries,
  isLoading = false,
}: {
  availableGlossaries: LaraGlossary[];
  selectedGlossaries: string[];
  setSelectedGlossaries: (ids: string[]) => void;
  isLoading?: boolean;
}) {
  const glossaryStates = useMemo(() => {
    const states: { [key: string]: boolean } = {};
    availableGlossaries.forEach((glossary) => {
      states[glossary.id] = selectedGlossaries.includes(glossary.id);
    });
    return states;
  }, [selectedGlossaries, availableGlossaries]);

  const handleChange =
    (glossaryId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedGlossaries([...selectedGlossaries, glossaryId]);
      } else {
        setSelectedGlossaries(
          selectedGlossaries.filter((id) => id !== glossaryId)
        );
      }
    };

  const handleSelectAll = () => {
    const allSelected = availableGlossaries.every(
      (glossary) => glossaryStates[glossary.id]
    );

    if (allSelected) {
      setSelectedGlossaries([]);
    } else {
      setSelectedGlossaries(availableGlossaries.map((g) => g.id));
    }
  };

  return (
    <CollapsibleFormElement title="AI Translation Glossaries">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        Select glossaries to use during AI translation. Glossaries ensure
        consistent terminology across all translations.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : availableGlossaries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No glossaries available.
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      availableGlossaries.length > 0 &&
                      availableGlossaries.every(
                        (glossary) => glossaryStates[glossary.id]
                      )
                    }
                    indeterminate={
                      availableGlossaries.some(
                        (glossary) => glossaryStates[glossary.id]
                      ) &&
                      !availableGlossaries.every(
                        (glossary) => glossaryStates[glossary.id]
                      )
                    }
                    onChange={handleSelectAll}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="bold">
                    Select all glossaries
                  </Typography>
                }
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {availableGlossaries.map((glossary) => (
                <Box
                  key={glossary.id}
                  sx={{
                    flexBasis: {
                      xs: "100%",
                      sm: "calc(50%)",
                      md: "calc(50%)",
                    },
                    flexGrow: 0,
                    flexShrink: 0,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!glossaryStates[glossary.id]}
                        onChange={handleChange(glossary.id)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" noWrap>
                        {glossary.name}
                      </Typography>
                    }
                    sx={{
                      marginRight: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 42px)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Paper>
    </CollapsibleFormElement>
  );
}
